import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Checkbox,
  NumberInput,
  NumberInputField,
  FormControl,
  FormLabel,
  Select,
  useDisclosure,
  useToast,
  Text,
} from '@chakra-ui/react';
import { cqAPI } from '../../lib/Auth'; // Adjust the import path
import moment from 'moment-timezone';

// Import Font Awesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faRedo, faStop, faPlay, faPlus } from '@fortawesome/free-solid-svg-icons';

function Jobs() {
  const [jobsData, setJobsData] = useState([]);
  const toast = useToast();
  const {
    isOpen: isRunModalOpen,
    onOpen: onRunModalOpen,
    onClose: onRunModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddJobModalOpen,
    onOpen: onAddJobModalOpen,
    onClose: onAddJobModalClose,
  } = useDisclosure();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [runTime, setRunTime] = useState('');

  // State for Add Job Modal
  const [capability, setCapability] = useState('');
  const [params, setParams] = useState('');
  const [scheduleFor, setScheduleFor] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [loop, setLoop] = useState(false);
  const [interval, setInterval] = useState('');

  const fetchJobs = async () => {
    try {
      const response = await cqAPI.get('/jobs/list');
      setJobsData(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({
        title: 'Error fetching jobs.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleAction = async (actionType, jobId) => {
    try {
      let response;
      if (actionType === 'restart') {
        response = await cqAPI.get(`/jobs/restart?job_id=${jobId}`);
      } else if (actionType === 'stop') {
        response = await cqAPI.get(`/jobs/stop?job_id=${jobId}`);
      } else if (actionType === 'run') {
        setSelectedJobId(jobId);
        onRunModalOpen();
        return;
      }
      if (response.type === 'ok') {
        fetchJobs();
      } else {
        toast({
          title: `Error ${actionType}ing job.`,
          description: response.msg || 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error(`Error ${actionType}ing job:`, error);
      toast({
        title: `Error ${actionType}ing job.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const submitRunJob = async () => {
    if (!runTime) {
      toast({
        title: 'Missing time.',
        description: 'Please enter the time.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    try {
      const response = await cqAPI.get(`/jobs/run?job_id=${selectedJobId}&time=${runTime}`);
      if (response.type === 'ok') {
        fetchJobs();
        onRunModalClose();
        setRunTime('');
      } else {
        toast({
          title: 'Error running job.',
          description: response.msg || 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error running job:', error);
      toast({
        title: 'Error running job.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Function to handle adding a new job
  const submitNewJob = async () => {
    // Validate inputs
    if (!capability || !params || !scheduleFor) {
      toast({
        title: 'Missing fields.',
        description: 'Please fill in all required fields.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    let parsedParams;
    try {
      parsedParams = JSON.parse(params);
    } catch (error) {
      toast({
        title: 'Invalid Params.',
        description: 'Params must be a valid JSON object.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (loop && !interval) {
      toast({
        title: 'Missing interval.',
        description: 'Please enter the interval in seconds.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      let scheduleForDate;

      if (timezone === 'local') {
        // The input is in local time, we need to convert it to UTC
        scheduleForDate = moment(scheduleFor);
        scheduleForDate = scheduleForDate.utc();
      } else {
        // The input is in local time, but the user wants it to be treated as UTC
        // So we need to parse the input as UTC
        scheduleForDate = moment.utc(scheduleFor);
      }

      const scheduleForUTC = scheduleForDate.toISOString();

      const data = {
        capability,
        params: parsedParams,
        schedule_for: scheduleForUTC,
        loop,
        interval: loop ? parseInt(interval) : undefined,
      };

      const response = await cqAPI.post('/jobs/create', data);

      if (response.type === 'ok') {
        fetchJobs();
        onAddJobModalClose();
        // Reset form fields
        setCapability('');
        setParams('');
        setScheduleFor('');
        setTimezone('UTC');
        setLoop(false);
        setInterval('');
      } else {
        toast({
          title: 'Error creating job.',
          description: response.msg || 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error creating job:', error);
      toast({
        title: 'Error creating job.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%" position="relative">
      <Box mb={4} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <FontAwesomeIcon icon={faTasks} size="2x" />
          <Text fontSize="2xl" ml={2}>
            Jobs Management
          </Text>
        </Box>
        <Button
          colorScheme="teal"
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={onAddJobModalOpen}
        >
          Add Job
        </Button>
      </Box>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Capability</Th>
            <Th>Running</Th>
            <Th>Scheduled</Th>
            <Th>Finished</Th>
            <Th>Failed</Th>
            <Th>Last Run (PST)</Th>
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {jobsData.map((job, index) => (
            <Tr key={index}>
              <Td>{job.capability}</Td>
              <Td>{String(job.running)}</Td>
              <Td>{String(job.scheduled)}</Td>
              <Td>{String(job.finished)}</Td>
              <Td>{String(job.failed)}</Td>
              <Td>{job.lastRunPST}</Td>
              <Td>
                {job.action ? (
                  <Button
                    size="sm"
                    onClick={() => handleAction(job.action.type, job.action.job_id)}
                    leftIcon={
                      job.action.type === 'restart' ? (
                        <FontAwesomeIcon icon={faRedo} />
                      ) : job.action.type === 'stop' ? (
                        <FontAwesomeIcon icon={faStop} />
                      ) : job.action.type === 'run' ? (
                        <FontAwesomeIcon icon={faPlay} />
                      ) : null
                    }
                  >
                    {job.action.label}
                  </Button>
                ) : null}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Modal for Time Input (Run Job) */}
      <Modal
        isOpen={isRunModalOpen}
        onClose={() => {
          onRunModalClose();
          setRunTime('');
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Enter Time</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={2}>Enter the time of day you want this task to run (HH:MM).</Text>
            <Input
              placeholder="Time of day (HH:MM)"
              value={runTime}
              onChange={(e) => setRunTime(e.target.value)}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                onRunModalClose();
                setRunTime('');
              }}
            >
              Close
            </Button>
            <Button colorScheme="blue" onClick={submitRunJob} ml={3}>
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal for Adding a New Job */}
      <Modal
        isOpen={isAddJobModalOpen}
        onClose={() => {
          onAddJobModalClose();
          // Reset form fields when modal closes
          setCapability('');
          setParams('');
          setScheduleFor('');
          setTimezone('UTC');
          setLoop(false);
          setInterval('');
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Job</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="capability" isRequired mb={4}>
              <FormLabel>Capability</FormLabel>
              <Input
                placeholder="Enter capability"
                value={capability}
                onChange={(e) => setCapability(e.target.value)}
              />
            </FormControl>
            <FormControl id="params" isRequired mb={4}>
              <FormLabel>Params (JSON format)</FormLabel>
              <Input
                placeholder='e.g., {"bid": "aaa", "second": 12}'
                value={params}
                onChange={(e) => setParams(e.target.value)}
              />
            </FormControl>
            <FormControl id="scheduleFor" isRequired mb={4}>
              <FormLabel>Schedule For</FormLabel>
              <Input
                type="datetime-local"
                value={scheduleFor}
                onChange={(e) => setScheduleFor(e.target.value)}
              />
            </FormControl>
            <FormControl id="timezone" isRequired mb={4}>
              <FormLabel>Time Zone</FormLabel>
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                <option value="UTC">UTC</option>
                <option value="local">Local Time Zone</option>
              </Select>
            </FormControl>
            <FormControl display="flex" alignItems="center" mb={4}>
              <Checkbox
                isChecked={loop}
                onChange={(e) => setLoop(e.target.checked)}
                mr={2}
              >
                Loop
              </Checkbox>
            </FormControl>
            <FormControl id="interval" mb={4} isDisabled={!loop}>
              <FormLabel>Interval (seconds)</FormLabel>
              <NumberInput
                min={1}
                value={interval}
                onChange={(valueString) => setInterval(valueString)}
              >
                <NumberInputField />
              </NumberInput>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => {
                onAddJobModalClose();
                // Reset form fields
                setCapability('');
                setParams('');
                setScheduleFor('');
                setTimezone('UTC');
                setLoop(false);
                setInterval('');
              }}
            >
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={submitNewJob} ml={3}>
              Add Job
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Jobs;
